import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from './lib/invalidate-loan-activity';
import { api } from '@/lib/api';

export type CommunicationType =
  'EMAIL' | 'PHONE_CALL' | 'MEETING' | 'LETTER' | 'INTERNAL_NOTE' | 'ENQUIRY';
export type CommunicationStatus =
  'OPEN' | 'IN_PROGRESS' | 'AWAITING_RESPONSE' | 'RESOLVED' | 'CLOSED';

export interface LoanCommunication {
  id: string;
  loanId: string;
  referenceNo: string;
  type: CommunicationType;
  direction: string;
  subject: string;
  body?: string;
  fromEmail?: string;
  toEmail?: string;
  contactName?: string;
  status: CommunicationStatus;
  priority: string;
  parentId?: string;
  tags?: string[];
  attachmentCount: number;
  createdById: string;
  assignedToId?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; firstName: string; lastName: string };
  assignedTo?: { id: string; firstName: string; lastName: string };
  replies?: LoanCommunication[];
}

// Custom hook with extra query params (type, status, search) — keep manual
export function useCommunications(
  loanId: string,
  filters?: { type?: string; status?: string; search?: string },
) {
  const params = new URLSearchParams();
  if (filters?.type) params.set('type', filters.type);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);

  return useQuery<LoanCommunication[]>({
    queryKey: ['communications', loanId, filters],
    queryFn: async () => {
      const qs = params.toString();
      const { data } = await api.get(`/loans/${loanId}/communications${qs ? `?${qs}` : ''}`);
      return data;
    },
    enabled: !!loanId,
  });
}

// Custom endpoint — keep manual
export function useCreateCommunication(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: any) => {
      const { data } = await api.post(`/loans/${loanId}/communications`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// Custom endpoint /reply — keep manual
export function useReplyCommunication(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cid, ...input }: any) => {
      const { data } = await api.post(`/loans/${loanId}/communications/${cid}/reply`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// Custom endpoint /resolve — keep manual
export function useResolveCommunication(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cid: string) => {
      const { data } = await api.post(`/loans/${loanId}/communications/${cid}/resolve`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

export const COMMUNICATION_TYPE_LABELS: Record<CommunicationType, string> = {
  EMAIL: 'Email',
  PHONE_CALL: 'Phone Call',
  MEETING: 'Meeting',
  LETTER: 'Letter',
  INTERNAL_NOTE: 'Internal Note',
  ENQUIRY: 'Enquiry',
};

export const COMMUNICATION_STATUS_LABELS: Record<
  CommunicationStatus,
  { label: string; color: string; bg: string }
> = {
  OPEN: { label: 'Open', color: '#3b82f6', bg: '#eff6ff' },
  IN_PROGRESS: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb' },
  AWAITING_RESPONSE: { label: 'Awaiting Response', color: '#8b5cf6', bg: '#f5f3ff' },
  RESOLVED: { label: 'Resolved', color: '#22c55e', bg: '#f0fdf4' },
  CLOSED: { label: 'Closed', color: '#6b7280', bg: '#f9fafb' },
};
